import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Form, Input, Modal } from 'antd';
import { useState } from 'react';
import { formSchemaColumns } from './columns';
import { FormSchemaFilters } from './FormSchemaFilters';

export const Route = createFileRoute('/console/$orgId/configuration/compliance/attestations/user-attestations')({
    staticData: {
        metadata: {
            title: 'User attestations',
        },
        ui: {
            title: 'User attestations configuration',
            subtitle: 'Manage user attestations',
        },
        modules: ['attestations'],
        permissions: ['settings:update'],
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <UserAttestations />
        </PageController>
    );
}

type QueryParams = RouterInputs['formSchema']['listFormSchemas'];

function UserAttestations() {
    const orgId = useOrgId();
    const { trpcUtils } = useTrpc();
    const { formatDate } = useFormatter();
    const navigate = useNavigate();
    const [duplicateModalState, setDuplicateModalState] = useState<{
        open: boolean;
        schemaId: bigint | null;
        originalKey: string;
        originalTitle: string;
    }>({
        open: false,
        schemaId: null,
        originalKey: '',
        originalTitle: '',
    });
    const [newSchemaKey, setNewSchemaKey] = useState('');
    const [newTitle, setNewTitle] = useState('');

    const { mutate: duplicateMutate, isPending: isDuplicating } = trpc.formSchema.duplicateFormSchema.useMutation();

    const [input, setInput] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { updatedAt: 'desc' },
    });

    const { data, isPending } = trpc.formSchema.listFormSchemas.useQuery(input);

    const handleDuplicate = (schemaId: bigint, schemaKey: string, title: string) => {
        setDuplicateModalState({
            open: true,
            schemaId,
            originalKey: schemaKey,
            originalTitle: title,
        });
        setNewSchemaKey(`${schemaKey}-copy`);
        setNewTitle(`${title} (Copy)`);
    };

    const handleDuplicateSubmit = () => {
        if (!duplicateModalState.schemaId) return;

        duplicateMutate(
            {
                schemaId: duplicateModalState.schemaId,
                newSchemaKey,
                newTitle,
            },
            {
                onSuccess: () => {
                    message.success('Form schema duplicated successfully');
                    setDuplicateModalState({ open: false, schemaId: null, originalKey: '', originalTitle: '' });
                    void trpcUtils.formSchema.listFormSchemas.invalidate();
                },
                onError: (error) => {
                    errorMessage.show(error);
                },
            },
        );
    };

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isPending,
        queryParams: input,
        onParamsChange: setInput,
        columns: formSchemaColumns(formatDate, {
            onDuplicate: handleDuplicate,
        }),
    });

    return (
        <>
            <Outlet />

            <TableToolbar
                filters={<FormSchemaFilters onChange={(where) => setInput((prev) => ({ ...prev, where }))} />}
                actions={
                    <Button.Add.Link
                        label="New form"
                        to="/console/$orgId/configuration/compliance/attestations/user-attestations/add"
                        params={{ orgId }}
                        variant="primary"
                    />
                }
            />

            <PrismaTable
                {...tableProps}
                size="small"
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/configuration/compliance/attestations/user-attestations/edit/$id',
                                params: { orgId, id },
                            },
                            e,
                        );
                    },
                })}
            />

            <Modal
                title="Duplicate form schema"
                open={duplicateModalState.open}
                onOk={handleDuplicateSubmit}
                onCancel={() =>
                    setDuplicateModalState({ open: false, schemaId: null, originalKey: '', originalTitle: '' })
                }
                okText="Duplicate"
                confirmLoading={isDuplicating}
            >
                <Form layout="vertical" className="mt-4">
                    <Form.Item label="New schema key" required>
                        <Input
                            value={newSchemaKey}
                            onChange={(e) => setNewSchemaKey(e.target.value)}
                            placeholder="annual-compliance-copy"
                        />
                        <span className="mt-1 text-subtle-foreground text-xs">
                            Only lowercase letters, numbers, and hyphens
                        </span>
                    </Form.Item>
                    <Form.Item label="New form title" required>
                        <Input
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            placeholder="Annual Compliance (Copy)"
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}
